import { createLogger } from '../logging/index.js';
import { RERANKER_REGISTRY } from '../models/registry.js';

const logger = createLogger('reranker-env');

export interface RerankerEnvOverrides {
  readonly enabled: boolean | undefined;
  readonly topK: number | undefined;
  readonly model: string | undefined;
}

/**
 * Parse and validate BK_RERANKER_ENABLED, BK_RERANKER_TOPK, and BK_RERANKER_MODEL
 * from environment variables.
 *
 * @param strict - true: throw on invalid values (benchmark flow).
 *                 false: warn + return undefined for that field (app runtime).
 */
export function parseRerankerEnvOverrides(strict: boolean): RerankerEnvOverrides {
  return {
    enabled: parseEnabled(process.env['BK_RERANKER_ENABLED'], strict),
    topK: parseTopK(process.env['BK_RERANKER_TOPK'], strict),
    model: parseModel(process.env['BK_RERANKER_MODEL'], strict),
  };
}

function parseEnabled(raw: string | undefined, strict: boolean): boolean | undefined {
  if (raw === undefined || raw === '') return undefined;
  if (raw === '1') return true;
  if (raw === '0') return false;
  const msg = `BK_RERANKER_ENABLED must be '0' or '1', got: "${raw}"`;
  if (strict) throw new Error(msg);
  logger.warn(msg);
  return undefined;
}

function parseTopK(raw: string | undefined, strict: boolean): number | undefined {
  if (raw === undefined || raw === '') return undefined;
  const parsed = Number.parseInt(raw, 10);
  if (Number.isNaN(parsed) || parsed < 1) {
    const msg = `BK_RERANKER_TOPK must be a positive integer, got: "${raw}"`;
    if (strict) throw new Error(msg);
    logger.warn(msg);
    return undefined;
  }
  return parsed;
}

function parseModel(raw: string | undefined, strict: boolean): string | undefined {
  if (raw === undefined || raw === '') return undefined;
  const entry = RERANKER_REGISTRY[raw];
  if (entry === undefined) {
    const valid = Object.keys(RERANKER_REGISTRY).join(', ');
    const msg = `BK_RERANKER_MODEL must be one of [${valid}], got: "${raw}"`;
    if (strict) throw new Error(msg);
    logger.warn(msg);
    return undefined;
  }
  return entry.id;
}
